import { User, UserV2 } from '@/types/players.types';

function toISOStringOrNull(value?: Date | string): string | null {
  if (!value) return null;
  return typeof value === 'string' ? value : value.toISOString();
}

function trimOrEmpty(value?: string | null): string {
  return value?.trim() ?? '';
}

export function mapUserToApiPayload(user: User): UserV2 {
  return {
    uuid: user.uuid,
    platformUuid: user.platformUuid,
    firstName: trimOrEmpty(user.firstName),
    middleName: trimOrEmpty(user.middleName),
    lastName: trimOrEmpty(user.lastName),
    nameSuffix: user.nameSuffix,
    gender: user.gender,
    dob: toISOStringOrNull(user.dob),
    address1: trimOrEmpty(user.address1),
    address2: trimOrEmpty(user.address2),
    countryId: user.country?.id?.toString() ?? null,
    stateId: user.state?.id?.toString() ?? null,
    city: trimOrEmpty(user.city),
    zip: trimOrEmpty(user.zip),
    altAddress1: trimOrEmpty(user.altAddress1),
    altAddress2: trimOrEmpty(user.altAddress2),
    altCity: trimOrEmpty(user.altCity),
    altZip: trimOrEmpty(user.altZip),
    phone: trimOrEmpty(user.phone),
    sendTextAlerts: user.sendTextAlerts,
    sendTextAlertsDate: toISOStringOrNull(user.sendTextAlertsDate),
    email: trimOrEmpty(user.email),
    emergencyContactName: trimOrEmpty(user.emergencyContactName),
    emergencyContactPhone: trimOrEmpty(user.emergencyContactPhone),
    profileImage: user.profileImage,
    nickname: user.nickname,
    facebook: user.facebook,
    snapchat: user.snapchat,
    twitter: user.twitter,
    youtube: user.youtube,
    instagram: user.instagram,
    linkedIn: user.linikedIn,
    dateCreated: toISOStringOrNull(user.dateCreated),
    createdByFullName: user.createdByFullName,
    dateUpdated: toISOStringOrNull(user.dateUpdated),
    updatedByFullName: user.updatedByFullName,
    dateDisabled: toISOStringOrNull(user.dateDisabled),
    disabledByFullName: user.disabledByFullName,
    phoneAreaCode: user.phoneAreaCode,
    emergencyContactPhoneAreaCode: user.emergencyContactPhoneAreaCode,
    languageFamilyId: user.languageFamilyId.toString(),
    handed: user.handed,
    heightFeet: user.heightFeet.toString(),
    heightInches: user.heightInches.toString(),
    showAgeToPublic: user.showAgeToPublic,
    unsubscribeEmarketingPickleball: user.unsubscribeEmarketingPickleball,
    unsubscribeEmarketingPickleballSms: user.unsubscribeEmarketingPickleballSms,
    unsubscribeEmarketing3rdParty: user.unsubscribeEmarketing3rdParty,
    dateClaimed: toISOStringOrNull(user.dateClaimed),
    timezoneId: user.timezone?.id?.toString() ?? null,
    platforms: user.platforms.map((p) => ({
      id: p.id.toString(),
      name: p.name,
      platformUserUuid: p.platformUserUuid,
    })),
    governingBodies: user.governingBodies.map((g) => ({
      id: g.id.toString(),
      name: g.name,
      shortName: g.shortName,
      memberNumber: g.memberNumber,
    })),
    altCountryId: user.altCountry?.id?.toString() ?? null,
    altStateId: user.altState?.id?.toString() ?? null,
    totalFollowers: user.totalFollowers.toString(),
    totalFollowing: user.totalFollowing.toString(),
    isSubRecord: user.isSubRecord,
    isTest: user.isTest,
    isCompleted: user.isCompleted,
    emergencyContactPhoneCountryId: user.emergencyContactPhoneCountryId.toString(),
    phoneCountryId: user.phoneCountryId.toString(),
    createdForModuleUuid: user.createdForModuleUuid,
    selfSkillDoublesId: user.selfSkillDoubles?.id?.toString() ?? null,
    selfSkillMixedId: user.selfSkillMixed?.id?.toString() ?? null,
    selfSkillSinglesId: user.selfSkillSingles?.id?.toString() ?? null,
    selfSkillSkinnySinglesId: user.selfSkillSkinnySingles?.id?.toString() ?? null,
    pickleballUSAPAUTPRSingles: user.pickleballUSAPAUTPRSingles,
    pickleballUSAPAUTPRSkinnySingles: user.pickleballUSAPAUTPRSkinnySingles,
    pickleballUSAPAUTPRDoubles: user.pickleballUSAPAUTPRDoubles,
    pickleballUSAPAUTPRMixed: user.pickleballUSAPAUTPRMixed,
    worldPickleballRatingSingles: user.worldPickleballRatingSingles,
    worldPickleballRatingDoubles: user.worldPickleballRatingDoubles,
    worldPickleballRatingMixed: user.worldPickleballRatingMixed,
    geniusSportsSinglesUuid: user.geniusSportsSinglesUuid ?? '',
    duprId: user.duprId,
    duprDoubles: user.duprDoubles,
    duprSingles: user.duprSingles,
    tiktok: user.tiktok,
    showCityToPublic: user.showCityToPublic,
    showStateToPublic: user.showStateToPublic,
    showOnlyFirstInitialOfLastNameToPublic: user.showOnlyFirstInitialOfLastNameToPublic,
    proLevelId: user.proLevelId.toString(),
    singleRating: user.singleRating ?? 0,
    doublesRating: user.doublesRating ?? 0,
    mixedRating: user.mixedRating ?? 0,
    skinnySinglesRating: user.skinnySinglesRating ?? 0,
    numberOfPlayedSingleGames: user.numberOfPlayedSingleGames ?? 0,
    numberOfPlayedDoublesGames: user.numberOfPlayedDoublesGames ?? 0,
    numberOfPlayedMixedGames: user.numberOfPlayedMixedGames ?? 0,
    numberOfPlayedSkinnySinglesGames: user.numberOfPlayedSkinnySinglesGames ?? 0,
    pls: user.pls,
    bio: user.bio,
    sponsorImg: user.sponsorImg,
    featuredImg: user.featuredImg,
    featuredVideo: user.featuredVideo,
    representingCountryId: user.representingCountry?.id?.toString() ?? null,
    turnedPro: toISOStringOrNull(user.turnedPro),
    sponsors: user.sponsors,
    images: user.images,
    biography: user.biography,
    pickleballUsapaNumber: user.pickleballUsapaNumber,
    pickleballCanadaNumber: user.pickleballCanadaNumber,
    pickleballLifetimeNumber: user.pickleballLifetimeNumber,
    pickleballSsipaNumber: user.pickleballSsipaNumber,
    pickleballPaaNumber: user.pickleballPaaNumber,
    pbUuid: user.pbUuid,
    ratingsReliabilitySingles: user.ratingsReliabilitySingles,
    ratingsReliabilitySkinnySingles: user.ratingsReliabilitySkinnySingles,
    ratingsReliabilityDoubles: user.ratingsReliabilityDoubles,
    ratingsReliabilityMixed: user.ratingsReliabilityMixed,
    slug: user.slug,
    dateMerged: toISOStringOrNull(user.dateMerged),
    latitude: user.latitude,
    longitude: user.longitude,
    alternateLatitude: user.alternateLatitude,
    alternateLongitude: user.alternateLongitude,
    bodyWeight: user.bodyWeight.toString(),
    backhand: user.backhand,
    coachFirstName: trimOrEmpty(user.coachFirstName),
    coachLastName: trimOrEmpty(user.coachLastName),
    lastActiveDate: toISOStringOrNull(user.lastActiveDate),
    stripeCustomerId: user.stripeCustomerId,
    duprDateLinkUpdated: user.duprDateLinkUpdated,
    socialLoginId: user.socialLoginId,
    socialProvider: user.socialProvider,
    showMiddleNameToPublic: user.showMiddleNameToPublic,
    hideBackToTop: user.hideBackToTop,
    hideQuickLaunchButtons: user.hideQuickLaunchButtons,
    canLogin: user.canLogin,
    hideOnSearch: user.hideOnSearch,
  };
}
